"use client";

import { useEffect, useMemo, useState } from "react";
import { resolvePath } from "./binding";
import { arraysEqual, buildRollPickerSnapshot, parseRollPickerColumns, resolveRollPickerDisplayIndexes, resolveRollPickerRawSelection, RollPickerFrame } from "./rollPickerShared";
import type { MacaronRenderContext, RecordAny } from "./types";

export function MacaronRollPicker({ config, ctx }: { config: RecordAny; ctx: MacaronRenderContext }) {
  const columns = useMemo(() => parseRollPickerColumns(config.columns, ctx.data), [config.columns, ctx.data]);
  const externalSelection = useMemo(() => resolveRollPickerRawSelection(config.selection, ctx.data), [config.selection, ctx.data]);
  const controlledIndexes = useMemo(() => resolveRollPickerDisplayIndexes(columns, externalSelection), [columns, externalSelection]);
  const [indexes, setIndexes] = useState<number[]>(() => controlledIndexes);
  const selectionPath = resolvePath(config.selection);
  const hasCustomSelectionPath = resolvePath(config.hasCustomSelection);
  const updateData = ctx.updateData;

  useEffect(() => {
    setIndexes((current) => (arraysEqual(current, controlledIndexes) ? current : controlledIndexes));
  }, [controlledIndexes]);

  const snapshot = useMemo(() => buildRollPickerSnapshot(columns, indexes), [columns, indexes]);
  useEffect(() => {
    if (selectionPath) updateData?.(selectionPath, snapshot.selectedValues);
    if (hasCustomSelectionPath) updateData?.(hasCustomSelectionPath, snapshot.hasCustomSelection);
  }, [hasCustomSelectionPath, selectionPath, snapshot, updateData]);

  if (columns.length === 0) return null;

  return (
    <RollPickerFrame
      columns={columns}
      indexes={indexes}
      onChange={(columnIndex, nextIndex) => {
        setIndexes((current) => current.map((value, index) => (index === columnIndex ? nextIndex : value)));
      }}
    />
  );
}
